import {makeAutoObservable, observable, reaction, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {ModalType, RequestState} from "data/enums";
import type {IModalPayload, IModalsStore} from "data/stores/modals/modals.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import {useNavigate} from "react-router-dom";
import type {IContextualPromptBannerStore} from "data/stores/contextual_prompt_banner/contextual_prompt_banner.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IGetShareImageParams, IShareImage} from "data/providers/api/predictions.api.provider";
import {MEDIA_URL} from "data/constants";
import type {ISharingStore} from "data/stores/sharing/sharing.store";

export interface IModalShareController extends ViewController {
	i18n: ILocalizationStore;

	get isOpen(): boolean;

	get contestId(): number;

	get modalContent(): IModalPayload | null;

	get playerFullName(): string | undefined;

	get shareImage(): IShareImage["shareImage"] | null;

	get imageUrl(): string | undefined;

	get isLoading(): boolean;

	close: () => void;
	downloadImage: () => void;
	shareLink: () => void;
}

@injectable()
export class ModalShareController implements IModalShareController {
	@observable private _navigate?: ReturnType<typeof useNavigate>;
	@observable _gameFeedId: number = 0;
	private _disposers: (() => void)[] = [];
	@observable _requestState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) public _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.SharingStore) private _sharingStore: ISharingStore,
		@inject(Bindings.ContextualPromptBannerStore)
		public _contextualPromptBanner: IContextualPromptBannerStore
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.SHARE_PICK;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	get playerFullName() {
		const playerId = this._modalsStore.modalContent?.playerId ?? 0;
		if (!playerId) return;

		const player = this._playerStore.getPlayerById(playerId);
		return `${String(player?.firstName)} ${String(player?.lastName)}`;
	}

	close = () => {
		this._modalsStore.hideModal();
	};

	get shareImage() {
		return this._sharingStore.shareWinnerImage;
	}

	get imageUrl() {
		const shareImage = this.shareImage;
		if (!shareImage) return;
		return `${MEDIA_URL}${shareImage.fileName}`;
	}

	get isLoading() {
		return [RequestState.PENDING, RequestState.IDLE].includes(this._requestState);
	}

	init() {
		this._disposers = [
			reaction(
				() => this.isOpen,
				(isOpen) => {
					if (isOpen) {
						this.fetchImage();
					}
				}
			),
		];
	}

	dispose() {
		this._disposers.forEach((d) => d());
	}

	get contestId() {
		const content = this.modalContent;
		return content?.contestId || 0;
	}

	private fetchImage() {
		const contestId = this.contestId;
		if (contestId) {
			this._requestState = RequestState.PENDING;
			const params: IGetShareImageParams = {
				contestId,
			};

			if (!this._userStore.isAuthorized) {
				params.guestUid = this._userStore.guestUid;
			}

			void this._sharingStore
				.requestWinnerImage(params)
				.then(() => {
					runInAction(() => {
						this._requestState = RequestState.SUCCESS;
					});
				})
				.catch((e) => {
					runInAction(() => {
						this._requestState = RequestState.ERROR;
					});
					console.error(e);
				});
		}
	}

	downloadImage = () => {
		const filename = `${this.playerFullName}_winner_${this.contestId}.png`;
		const url = this.imageUrl;
		if (!url) {
			this._modalsStore.showModal(ModalType.ERROR, {
				message: "There was a problem with the download",
			});
			return;
		}

		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	shareLink = () => {
		if (navigator.share) {
			navigator
				.share({
					title: this.i18n.t(
						this.modalContent?.title ?? "modal.share.share_title",
						"Thank You for Voting"
					),
					text: this.playerFullName,
					url: this.imageUrl,
				})
				.then(() => console.log("Shared successfully"))
				.catch((err) => {
					if (err.name === "AbortError") return;
					this._modalsStore.showModal(ModalType.ERROR, {
						message: this.i18n.t(
							"modal.share.error",
							"There was a problem with the download"
						),
					});
					return;
				});
		} else {
			this._modalsStore.showModal(ModalType.ERROR, {
				message: this.i18n.t("modal.share.error", "There was a problem with the download"),
			});
			return;
		}
	};
}
